// components/persona-guard.tsx
import { useEffect } from 'react'
import React from 'react'
import { useNavigate } from '@tanstack/react-router'
import { usePersonaStore } from '@/stores/personaStore'
import { cn } from '@/lib/utils'

// Component for redirecting if no persona is selected
export const PersonaRedirect = () => {
  const { currentPersonaId } = usePersonaStore()
  const navigate = useNavigate()

  useEffect(() => {
    const checkPersona = () => {
      const storedPersonaId = localStorage.getItem('currentPersonaId')
      if (!currentPersonaId && !storedPersonaId) {
        navigate({ to: '/' })
      }
    }

    checkPersona()
  }, [currentPersonaId, navigate])

  return null
}

// Component to wrap UI elements that require a persona
export const PersonaGuard = ({ children }: { children: React.ReactNode }) => {
  const { currentPersonaId } = usePersonaStore()
  const [isValidPersona, setIsValidPersona] = React.useState(false)

  React.useEffect(() => {
    const validatePersona = () => {
      const storedPersonaId = localStorage.getItem('currentPersonaId')
      setIsValidPersona(!!(currentPersonaId || storedPersonaId))
    }

    validatePersona()
    const storageListener = () => validatePersona()

    window.addEventListener('storage', storageListener)
    return () => window.removeEventListener('storage', storageListener)
  }, [currentPersonaId])

  return (
    <div className='relative group/persona-guard'>
      {!isValidPersona && (
        <div
          className='absolute inset-0 z-50 cursor-not-allowed'
          style={{
            pointerEvents: 'all',
            backgroundColor: 'rgba(255, 255, 255, 0.5)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className='absolute inset-0 bg-background/20' />
        </div>
      )}
      <div
        className={cn({
          'pointer-events-none opacity-100': !isValidPersona,
          'pointer-events-auto': isValidPersona,
        })}
      >
        {children}
      </div>
    </div>
  )
}

export const PersonaGuardInvisible = ({
  children,
  redirect = false,
}: {
  children: React.ReactNode
  redirect?: boolean
}) => {
  const { currentPersonaId } = usePersonaStore()
  const navigate = useNavigate()
  const [isValidPersona, setIsValidPersona] = React.useState(false)

  React.useEffect(() => {
    const validatePersona = () => {
      const storedPersonaId = localStorage.getItem('currentPersonaId')
      setIsValidPersona(!!(currentPersonaId || storedPersonaId))
    }

    validatePersona()
    const storageListener = () => validatePersona()

    window.addEventListener('storage', storageListener)
    return () => window.removeEventListener('storage', storageListener)
  }, [currentPersonaId])

  React.useEffect(() => {
    if (redirect && !isValidPersona) {
      navigate({ to: '/' })
    }
  }, [isValidPersona, navigate, redirect])

  if (!isValidPersona) {
    return redirect ? null : (
      <div className='fullhidden invisible absolute h-0 w-0'>{children}</div>
    )
  }

  return <>{children}</>
}

/*
<PersonaGuardInvisible>
  <SensitivePersonaContent />
</PersonaGuardInvisible>

<PersonaGuardInvisible redirect>
  <ProtectedPersonaDashboard />
</PersonaGuardInvisible>


*/

export const NoPersonaContent = ({
  children,
  fallback = null,
  redirect = false,
  redirectPath = '/',
}: {
  children: React.ReactNode
  fallback?: React.ReactNode
  redirect?: boolean
  redirectPath?: string
}) => {
  const { currentPersonaId } = usePersonaStore()
  const navigate = useNavigate()
  const [hasNoPersona, setHasNoPersona] = React.useState(false)

  React.useEffect(() => {
    const validatePersona = () => {
      const storedPersonaId = localStorage.getItem('currentPersonaId')
      setHasNoPersona(!currentPersonaId && !storedPersonaId)
    }

    validatePersona()
    const storageListener = () => validatePersona()

    window.addEventListener('storage', storageListener)
    return () => window.removeEventListener('storage', storageListener)
  }, [currentPersonaId])

  React.useEffect(() => {
    if (redirect && !hasNoPersona) {
      navigate({ to: redirectPath })
    }
  }, [hasNoPersona, navigate, redirect, redirectPath])

  if (!hasNoPersona) {
    return redirect ? null : fallback
  }

  return <>{children}</>
}

/*

<NoPersonaContent>
  <PersonaOnboardingScreen />
</NoPersonaContent>

<NoPersonaContent fallback={<p>Persona is already selected</p>}>
  <PersonaSelectionGuide />
</NoPersonaContent>


// routes/onboarding.tsx
function OnboardingRoute() {
  return (
    <NoPersonaContent redirect redirectPath="/dashboard">
      <NewUserWelcome />
    </NoPersonaContent>
  )
}



*/

export const PersonaSensitiveContent = ({
  children,
  fallback = null,
  redirect = false,
}: {
  children: React.ReactNode
  fallback?: React.ReactNode
  redirect?: boolean
}) => {
  const { currentPersonaId } = usePersonaStore()
  const navigate = useNavigate()
  const [hasPersona, setHasPersona] = React.useState(false)

  React.useEffect(() => {
    const validatePersona = () => {
      const storedPersonaId = localStorage.getItem('currentPersonaId')
      setHasPersona(!!(currentPersonaId || storedPersonaId))
    }

    validatePersona()
    const storageListener = () => validatePersona()

    window.addEventListener('storage', storageListener)
    return () => window.removeEventListener('storage', storageListener)
  }, [currentPersonaId])

  React.useEffect(() => {
    if (redirect && !hasPersona) {
      navigate({ to: '/' })
    }
  }, [hasPersona, navigate, redirect])

  if (!hasPersona) {
    return redirect ? null : fallback
  }

  return <>{children}</>
}

export default PersonaSensitiveContent

/*

<PersonaSensitiveContent>
  <PersonaOnlyDashboard />
</PersonaSensitiveContent>

*/
